import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form"
import { useFee } from "@/hooks/use-fee"
import { useGetPreviewFee } from "@/hooks/use-get-fee"
import { FeeCategory } from "@/lib/types"
import {
	downloadFileFromURl,
	cn,
	getFieldById,
	getNationalityByCoutry,
	parseAttributes,
} from "@/lib/utils"
import { useFormStore } from "@/stores/use-form-store"
import {
	PaymentStatus,
	PaymentTwoConfig,
	type Attribute,
	type FEE_ITEM,
	type FIELD,
	// type PAYMENT_PLAN,
	type PAYMENT_VERIFIED_FIELDS,
} from "@/types/form-types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo, useRef, useState } from "react"
import { useForm, type UseFormReturn } from "react-hook-form"
import type z from "zod"
import { getPaymentOneFieldAttributes } from "../registration-fee"
import Actions from "./actions"
import LoanProviderForm from "./loan"
import Plans from "./plans"
import {
	combinedSchema,
	loanOfflinePaymentSchema,
	offlinePaymentSchema,
} from "./schemas"
import { useHandlePayment } from "./self/hooks/use-handle-payment"
import { progressivePoll } from "./self/hooks/use-polling"
import {
	CheckCircle2,
	CircleX,
	Download,
	Info,
	Loader2,
	RefreshCw,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { dummyPdfBytes } from "@/assets/dummy_bytes"
import { api } from "@/lib/api"
import { useShallow } from "zustand/react/shallow"
import Alert from "./alert"
import { useVerifiedFields } from "@/hooks/use-verified-fields"
import { PaymentSuccess } from "./payment-success"
import { logger } from "@/utils/logger"
import Coupons from "./coupons"
import { downloadFile, getPlanName, getPlanType } from "./utils"
import Summary from "./summary"
import { PaymentContext } from "./use-payment"
import { AxiosError } from "axios"
import Status from "./status"
import { format } from "date-fns"
import OfflinePayment from "./offline"
import {
	Alert as AlertStatus,
	AlertDescription,
	AlertTitle,
} from "@/components/ui/alert"
import { Switch } from "@/components/ui/switch"
import { useGetLoanProviders } from "@/hooks/use-loan"
import { PAYMENT_PLANS_PER_UNIVERSITY } from "@/lib/constants"

interface PaymentTwoFieldProps {
	config: FIELD
	form: UseFormReturn
}

export function getPaymentTwoFieldAttributes(attributes: Attribute[]) {
	const props: {
		self?: string[]
		loan?: string[]
		selfPlanTitles?: { id: string; title: string }[]
		loanPlanTitles?: { id: string; title: string }[]
		nameYourPayment?: string
		description?: string
		enableOfflinePayment?: boolean
		enableOfflineLoan?: boolean
		displayTheSelfAndLoanPaymentModesTogether?: boolean
		programFieldId?: string
		specializationFieldId?: string
		nationalityFieldId?: string
		registrationFieldId?: string
		periodNumber: number
	} = {
		periodNumber: 1,
	}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case PaymentTwoConfig.Self:
				props.self = value as string[]
				break
			case PaymentTwoConfig.Loan:
				props.loan = value as string[]
				break
			case PaymentTwoConfig.SelfPlanTitles:
				props.selfPlanTitles = value as { id: string; title: string }[]
				break
			case PaymentTwoConfig.LoanPlanTitles:
				props.loanPlanTitles = value as { id: string; title: string }[]
				break
			case PaymentTwoConfig.NameYourPayment:
				props.nameYourPayment = value as string
				break
			case PaymentTwoConfig.Description:
				props.description = value as string
				break
			case PaymentTwoConfig.EnableOfflinePayment:
				props.enableOfflinePayment = value as boolean
				break
			case PaymentTwoConfig.EnableOfflineLoan:
				props.enableOfflineLoan = value as boolean
				break
			case PaymentTwoConfig.DisplayTheSelfAndLoanPaymentModesTogether:
				props.displayTheSelfAndLoanPaymentModesTogether =
					value as boolean
				break
			case PaymentTwoConfig.SpecializationFieldId:
				props.specializationFieldId = value as string
				break
			case PaymentTwoConfig.ProgramFieldId:
				props.programFieldId = value as string
				break
			case PaymentTwoConfig.NationalityFieldId:
				props.nationalityFieldId = value as string
				break
			case PaymentTwoConfig.RegistrationFieldId:
				props.registrationFieldId = value as string
				break
			case PaymentTwoConfig.PeriodNumber:
				props.periodNumber = value as number
		}
	})

	return props
}

const Payment = ({ form, config }: PaymentTwoFieldProps) => {
	const {
		// isPreview,
		config: formConfig,
		applicationSubmission,
		applicationId,
		setVerifiedFields,
		verifiedFields,
		isRenewal,
	} = useFormStore(
		useShallow(state => ({
			// isPreview: state.isPreview,
			config: state.config,
			applicationSubmission: state.applicationSubmission,
			applicationId: state.applicationId,
			verifiedFields: state.verifiedFields,
			setVerifiedFields: state.setVerifiedFields,
			isRenewal: state.isRenewal,
		})),
	)

	const isPreview = !applicationId || applicationId === ""

	const programTypeFieldId = useMemo(() => {
		for (const stage of formConfig?.stages ?? []) {
			for (const section of stage.sections ?? []) {
				for (const row of section.rows ?? []) {
					for (const column of row.columns ?? []) {
						const field = column.fields?.find(
							f => f.field_type === "PROGRAM_TYPE",
						)
						if (field) return field.field_id
					}
				}
			}
		}
		return undefined
	}, [formConfig])

	const { setValue, getValues: getFieldValues } = form
	const formState = getFieldValues(config.field_id)
	const stageEntryTimestamp = useRef<number>(Date.now())
	// Store stable courseID to prevent payment status refetch when payment_type changes
	const stableCourseIdRef = useRef<string>("")

	// Convert timestamp to the desired format
	const formatTimestamp = (timestamp: number) => {
		const date = new Date(timestamp)
		const isoString = date.toISOString() // Returns in 'YYYY-MM-DDTHH:mm:ss.sssZ'

		// Extract milliseconds and add some fraction (you can adjust this if needed)
		const milliseconds = date.getMilliseconds()
		const fractionalMilliseconds = Math.random().toFixed(5).slice(2) // Add up to 5 decimal places
		const formatted = isoString.replace(
			/\.(\d{3})Z$/,
			`.${milliseconds}${fractionalMilliseconds}Z`,
		)

		return formatted
	}

	const formattedTimestamp = formatTimestamp(stageEntryTimestamp.current)

	// initialize default Values
	useEffect(() => {
		if (formState == "" || formState == undefined) {
			setValue(config.field_id, {
				payment_type: "self" as "self" | "loan",
				loan_payment_mode: "apply-loan" as
					| "apply-loan"
					| "loan-disbursed",
				self_payment_mode: "pay-online" as
					| "pay-online"
					| "already-paid",
				loan_provider_name: "FIBE",
				selected_payment_option_id: "",
				order_id: "",
				transaction_id: "",
				payment_status: "",
				loan_status: "PENDING" as "DISBURED" | "REJECTED" | "PENDING",
				loan_amount: 0,
				stage_check_in_time: formattedTimestamp,
				loan_provider_id: "",
				loan_tenure: 0,
				required_fields: {},
			})
		}
	}, [formState])

	// Plans Section Attributes
	const {
		displayTheSelfAndLoanPaymentModesTogether,
		description,
		nameYourPayment,
		enableOfflinePayment,
		enableOfflineLoan,
	} = getPaymentTwoFieldAttributes(config.attributes)
	// Payment Plans
	// Hooks
	const {
		useGetFeeGridByBatchId,
		useDiscountedFee,
		useGetPaymentPlanPrices,
		useGetFeeGridByApplicationId,
	} = useFee()
	const { useGetCourseFee, useGetFeeGridBasedOnPaymentPlan } =
		useGetPreviewFee()
	const { data } = useGetFeeGridByBatchId(
		formConfig?.university_id || "",
		formConfig?.batch_id || "",
	)
	const {
		self,
		loan,
		programFieldId,
		specializationFieldId,
		nationalityFieldId,
		registrationFieldId,
		selfPlanTitles,
		loanPlanTitles,
		periodNumber,
	} = getPaymentTwoFieldAttributes(config.attributes)
	const { nonCourseFeeIds } = getPaymentOneFieldAttributes(
		getFieldById(formConfig || undefined, registrationFieldId)
			?.attributes || [],
	)

	// --------
	// Watchers
	// const paymentStatusWatcher = form.watch(config.field_id + ".payment_status")
	const selectedProgram = form.watch(programFieldId!)
	const selectedSpecialization = form.watch(specializationFieldId!)
	const selectedNationality = form.watch(nationalityFieldId!)
	const selectedProgramType = form.watch(programTypeFieldId!)
	// --------
	// Constants
	const programId = selectedProgram || applicationSubmission[programFieldId!]
	const specializationId =
		selectedSpecialization || applicationSubmission[specializationFieldId!]
	const nationalityId =
		selectedNationality || applicationSubmission[nationalityFieldId!]
	const programType =
		selectedProgramType || applicationSubmission[programTypeFieldId!]
	const allIds = Array.from(new Set([...(self || []), ...(loan || [])]))
	const [activeTab, setActiveTab] = useState("self-payment")
	// --------
	// API Calls
	const { data: paymentPlansPrices } = useGetPaymentPlanPrices({
		universityId: formConfig?.university_id || "",
		batchId: formConfig?.batch_id || "",
		programId: programId,
		specilizationId: specializationId,
		nationalityId: getNationalityByCoutry(nationalityId),
		componentIds: allIds || [],
		usedComponentIds: [],
		applicationId: applicationId || "",
		periodNumber: periodNumber,
		isRenewal: isRenewal,
	})

	// --------
	// Constants
	const allPlans = useDiscountedFee({
		feeComponents: paymentPlansPrices?.components || [],
		isRenewal: isRenewal,
	})
	// --------
	// Return Plans
	const activeSelfPlans = isPreview
		? useGetCourseFee(data || [], self || []).map(plan => ({
				...plan,
				name:
					selfPlanTitles?.find(
						title => title.id === plan.component_id,
					)?.title || plan.name,
			}))
		: allPlans.reduce((acc: FEE_ITEM[], plan) => {
				if (isRenewal || (self || []).includes(plan.component_id)) {
					acc.push({
						...plan,
						name:
							selfPlanTitles?.find(
								title => title.id === plan.component_id,
							)?.title || plan.name,
					})
				}
				return acc
			}, [])
	const activeLoanPlans = isPreview
		? useGetCourseFee(data || [], loan || []).map(plan => ({
				...plan,
				name:
					loanPlanTitles?.find(
						title => title.id === plan.component_id,
					)?.title || plan.name,
			}))
		: allPlans.reduce((acc: FEE_ITEM[], plan) => {
				if (paymentPlansPrices && paymentPlansPrices.is_last_semester) {
					return []
				}

				if (isRenewal) {
					const universityPlans = formConfig?.university_id
						? PAYMENT_PLANS_PER_UNIVERSITY[
								formConfig?.university_id || ""
							]
						: []

					if (
						plan.frequency === "ONE_TIME" ||
						universityPlans?.includes(plan.frequency)
					) {
						acc.push({
							...plan,
							name:
								loanPlanTitles?.find(
									title => title.id === plan.component_id,
								)?.title || plan.name,
						})
					}
				} else {
					if ((loan || []).includes(plan.component_id)) {
						acc.push({
							...plan,
							name:
								loanPlanTitles?.find(
									title => title.id === plan.component_id,
								)?.title || plan.name,
						})
					}
				}
				return acc
			}, [])

	const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(
		activeSelfPlans[0]
			? `self-${activeSelfPlans[0].component_id}`
			: activeLoanPlans[0]
				? `loan-${activeLoanPlans[0].component_id}`
				: "",
	)

	const selectedPaymentPlanFeeComponent = useMemo(() => {
		if (!selectedPaymentPlan || selectedPaymentPlan == "") return
		const normalizedSelectedPaymentPlan = selectedPaymentPlan.replace(
			/^(self|loan)-/,
			"",
		)

		if (selectedPaymentPlan.startsWith("self")) {
			return activeSelfPlans.find(
				plan => plan.component_id === normalizedSelectedPaymentPlan,
			)
		} else if (selectedPaymentPlan.startsWith("loan")) {
			return activeLoanPlans.find(
				plan => plan.component_id === normalizedSelectedPaymentPlan,
			)
		}
	}, [selectedPaymentPlan, activeLoanPlans, activeSelfPlans])

	// --------
	// Loan Provider Config
	const [formDisabled, setFormDisabled] = useState(false)
	const loanProviderForm = useForm<z.infer<typeof combinedSchema>>({
		resolver: zodResolver(combinedSchema),
		mode: "onChange",
		defaultValues: {
			applicant_pincode: "",
			applicant_first_name: "",
			applicant_last_name: "",
			applicant_full_name: "",
			applicant_email: "",
			otp_verified: false,
			applicant_dob: "",
			applicant_phone_number: "",
			country_code: "+91",
			applicant_pan_number: "",
			loan_provider_id: "",
		},
		disabled: formDisabled,
	})

	// Offline Payment Form
	const [selfOfflinePaymentFormDisabled] = useState(false)
	const selfOfflinePaymentForm = useForm<
		z.infer<typeof offlinePaymentSchema>
	>({
		resolver: zodResolver(offlinePaymentSchema),
		mode: "all",
		disabled: selfOfflinePaymentFormDisabled,
	})
	const [loanOfflinePaymentFormDisabled] = useState(false)
	const loanOfflinePaymentForm = useForm<
		z.infer<typeof loanOfflinePaymentSchema>
	>({
		resolver: zodResolver(loanOfflinePaymentSchema),
		mode: "all",
		disabled: loanOfflinePaymentFormDisabled,
	})

	// Functions
	const getValues = () => {
		return form.getValues(config.field_id)
	}

	const setValues = (values: any) => {
		setValue(config?.field_id, {
			...form.getValues(config.field_id),
			...values,
			stage_check_in_time: formattedTimestamp,
		})
	}

	const validate = () => {
		let isValid = false
		if (formState?.payment_type === "loan") {
			const { isValid: isFormValid } = loanProviderForm.formState
			isValid = isFormValid
		} else {
			// TODO : Add validation for self payment
		}
		return isValid
	}

	//In preview mode
	const { data: previewData } = useGetFeeGridBasedOnPaymentPlan(
		data || [],
		self || [],
		nonCourseFeeIds || [],
		selectedPaymentPlan.replace(/^(self|loan)-/, ""),
	)

	// const pp = form.watch(config?.field_id)
	// console.log("Field VAlue: ", pp)

	//In real mode to get the specific payment plan fee breakdown
	const { data: realData } = useGetFeeGridByApplicationId({
		universityId: formConfig?.university_id || "",
		batchId: formConfig?.batch_id || "",
		programId: programId,
		specilizationId: specializationId,
		nationalityId: getNationalityByCoutry(nationalityId),
		componentIds: selectedPaymentPlan
			? [selectedPaymentPlan.replace(/^(self|loan)-/, "")]
			: [],
		usedComponentIds: nonCourseFeeIds || [],
		applicationId: applicationId || "",
		paymentType: form.watch(config.field_id)?.payment_type,
		periodNumber: periodNumber,
		isRenewal: isRenewal,
		selectedPaymentPlan: getPlanName(
			selectedPaymentPlanFeeComponent?.frequency || "",
		),
	})

	// Update stable courseID ref when realData changes, but only if course_id actually exists
	useEffect(() => {
		if (
			realData?.course_id &&
			realData.course_id !== stableCourseIdRef.current
		) {
			stableCourseIdRef.current = realData.course_id
		}
	}, [realData?.course_id])

	// Memoize courseID to prevent unnecessary hook recreation
	const stableCourseId = useMemo(() => {
		return stableCourseIdRef.current || realData?.course_id || ""
	}, [realData?.course_id])

	const selectedPaymentPlanFee = useMemo(() => {
		if (isPreview) return previewData
		else {
			const discountedtFee =
				useDiscountedFee({
					feeComponents: realData?.components || [],
					couponDiscount: realData?.coupon_discount,
					paymentType: getValues()?.payment_type,
					lender_id:
						formState?.loan_payment_mode !== "loan-disbursed"
							? loanProviderForm.watch("loan_provider_id")
							: undefined,
					tenure_in_months:
						formState?.loan_payment_mode !== "loan-disbursed"
							? loanProviderForm.watch("loan_provider_tenure")
							: undefined,
					isRenewal: isRenewal,
					selectedPaymentPlan: selectedPaymentPlan,
				}) || []

			return discountedtFee
		}
	}, [
		previewData,
		realData,
		isPreview,
		getValues()?.payment_type,
		loanProviderForm.watch("loan_provider_id"),
		loanProviderForm.watch("loan_provider_tenure"),
	])

	// Memoize useHandlePayment parameters to prevent unnecessary hook recreation
	const handlePaymentParams = useMemo(
		() => ({
			mode: window.__DF_MODE,
			applicationID: applicationId || "",
			batchID: formConfig?.batch_id || "",
			courseID: stableCourseId,
			partnerID: formConfig?.partner_id || "",
			stage_check_in_time:
				formState?.stage_check_in_time ?? formattedTimestamp,
		}),
		[
			applicationId,
			formConfig?.batch_id,
			stableCourseId,
			formConfig?.partner_id,
			formState?.stage_check_in_time,
			formattedTimestamp,
		],
	)

	const {
		useCheckPaymentStatus,
		// useHandleDownloadReceipt,
	} = useHandlePayment(handlePaymentParams)

	const [isLoading, setIsLoading] = useState(false)

	// const { mutateAsync: downloadReceipt } = useHandleDownloadReceipt()

	const {
		data: paymentStatus,
		isLoading: isPaymentStatusLoading,
		refetch,
		error,
	} = useCheckPaymentStatus({
		category: FeeCategory.TUITION,
		semester: periodNumber,
	})

	const { data: loanProviders } = useGetLoanProviders({
		course_id: realData?.course_id ?? null,
	})

	const loanDetails = useMemo(() => {
		return paymentStatus?.loan_details
	}, [paymentStatus])

	const { useSetVerifiedFields } = useVerifiedFields()
	const { mutateAsync: setDisableFields } = useSetVerifiedFields(
		formConfig?.university_id || "",
		formConfig?.batch_id || "",
		applicationId || "",
	)

	const tempFunction = async () => {
		if (
			programFieldId &&
			verifiedFields.has(programFieldId) &&
			specializationFieldId &&
			verifiedFields.has(specializationFieldId) &&
			nationalityFieldId &&
			verifiedFields.has(nationalityFieldId)
		) {
			return
		}

		const allFields = [
			{ field_id: programFieldId, value: programId },
			{ field_id: specializationFieldId, value: specializationId },
			{ field_id: nationalityFieldId, value: nationalityId },
			{ field_id: programTypeFieldId, value: programType },
		]

		const filteredFields = allFields.filter(
			f =>
				f.field_id &&
				f.field_id !== "" &&
				f.value !== undefined &&
				f.value !== "",
		)

		const payload: PAYMENT_VERIFIED_FIELDS[] = filteredFields.map(f => ({
			field_id: f.field_id!,
			is_verified: true,
		}))

		const data = await setDisableFields(payload, {
			onSuccess: () => {
				logger.info("Verified Fields Set")
				setVerifiedFields(
					filteredFields.map(f => ({
						fieldId: f.field_id!,
						isVerified: true,
					})),
				)
			},
			onError: () => {
				logger.error("Verified Fields Not Set")
			},
		})

		if (data.status >= 200 && data.status < 300) {
			logger.info("Verified Fields Set")
			setVerifiedFields(
				filteredFields.map(f => ({
					fieldId: f.field_id!,
					isVerified: true,
				})),
			)
		}
	}

	// useEffect(() => {
	// 	console.log("[NEW]Inside useEffect")
	// 	if (
	// 		programFieldId &&
	// 		verifiedFields.has(programFieldId) &&
	// 		specializationFieldId &&
	// 		verifiedFields.has(specializationFieldId) &&
	// 		nationalityFieldId &&
	// 		verifiedFields.has(nationalityFieldId)
	// 	) {
	// 		return
	// 	}

	// 	console.log(
	// 		"[NEW]Inside useEffect has all fields",
	// 		form.watch(config?.field_id + ".payment_status"),
	// 	)
	// 	if (
	// 		form.watch(config?.field_id + ".payment_status") ===
	// 		PaymentStatus.PAYMENT_SUCCESS
	// 	) {
	// 		const payload: PAYMENT_VERIFIED_FIELDS[] = [
	// 			{
	// 				field_id: programFieldId || "",
	// 				value: programId,
	// 				is_verified: true,
	// 			},
	// 			{
	// 				field_id: specializationFieldId || "",
	// 				value: specializationId,
	// 				is_verified: true,
	// 			},
	// 			{
	// 				field_id: nationalityFieldId || "",
	// 				value: nationalityId,
	// 				is_verified: true,
	// 			},
	// 			{
	// 				field_id: programTypeFieldId || "",
	// 				value: programType,
	// 				is_verified: true,
	// 			},
	// 		]
	// 		console.log("[NEW]Function called with payload: ", payload)
	// 		tempFunction(payload)
	// 	}
	// }, [form.watch(config?.field_id + ".payment_status")])

	//dont include isDiscountComponent true
	const totalAmount =
		selectedPaymentPlanFee
			?.filter(item => !item.isDiscountComponent)
			.reduce((sum, item) => sum + (item.price || 0), 0) || 0

	//remove the finalPrice of the isDiscountComponent true and add the price of the isDiscountComponent true
	const totalAmountAfterDiscount =
		(selectedPaymentPlanFee
			?.filter(item => !item.isDiscountComponent)
			.reduce((sum, item) => sum + (item.finalPrice || 0), 0) || 0) -
		(selectedPaymentPlanFee
			?.filter(item => item.isDiscountComponent)
			.reduce((sum, item) => sum + (item.finalPrice || 0), 0) || 0)

	useEffect(() => {
		setValues({
			total_amount: totalAmount,
			final_payable_amount: totalAmountAfterDiscount,
		})
	}, [totalAmount, totalAmountAfterDiscount])

	// Log all form errors
	// useEffect(() => {
	// 	console.log("Form errors: ", globalFormState.errors)
	// }, [globalFormState.errors])
	const successStatuses = [
		PaymentStatus.PAYMENT_SUCCESS,
		PaymentStatus.LOAN_DISBURSEMENT_RECEIVED,
		PaymentStatus.OFFLINE_PAYMENT_APPROVED,
		PaymentStatus.OFFLINE_LOAN_APPROVED,
	]

	useEffect(() => {
		if (
			paymentStatus?.status &&
			successStatuses.includes(paymentStatus?.status as PaymentStatus)
		) {
			let hideDownloadReceipt = false;
			if (!paymentStatus?.details?.show_receipt) {
				hideDownloadReceipt = true;
			}
			setValues({
				payment_success: true,
				payment_status: paymentStatus?.status,
				order_id: paymentStatus?.details?.order_id,
				transaction_id: paymentStatus?.details?.transaction_id || "-",
				hideDownloadReceipt: hideDownloadReceipt,
				final_payable_amount: paymentStatus?.details?.amount,
				currency: paymentStatus?.details?.currency,
				paidAt: paymentStatus?.details?.updated_at,
			})

			tempFunction()

			return
		}

		setValues({
			// ...getValues(),
			total_amount: totalAmount,
			final_payable_amount: totalAmountAfterDiscount,
			order_id: paymentStatus?.details?.order_id,
			payment_status: paymentStatus?.status,
			transaction_id: paymentStatus?.details?.transaction_id,
		})

		if (paymentStatus?.status === PaymentStatus.PAYMENT_IN_PROGRESS) {
			let cancelled = false

			const startPolling = async () => {
				try {
					const result = await progressivePoll(
						async () =>
							// checkTransactionStatus({
							// 	orderID: paymentStatus?.details?.order_id,
							// 	transactionID:
							// 		paymentStatus?.details?.transaction_id,
							// }),
							await refetch(),
						{
							isDone: res => {
								if (!res) return false
								return [
									"PAYMENT_SUCCESS",
									"PAYMENT_FAILED_RETRY_ALLOWED",
								].includes(res?.data?.status || "")
							},
							timeout: 1 * 60 * 1000, // 1 minute
						},
					)

					if (!cancelled && result) {
						if (
							result.data?.status ===
							PaymentStatus.PAYMENT_SUCCESS
						) {
							setValues({
								payment_status: PaymentStatus.PAYMENT_SUCCESS,
							})
						} else if (
							result.data?.status ===
							PaymentStatus.PAYMENT_FAILED_RETRY_ALLOWED
						) {
							setValues({
								payment_status:
									PaymentStatus.PAYMENT_FAILED_RETRY_ALLOWED,
							})
						}
					}
				} catch (err: any) {
					logger.error("Error in polling", err)
					if (!cancelled) {
						if (err.name === "PollingTimeoutError") {
							// Timeout case
							setValues({
								payment_status:
									PaymentStatus.PAYMENT_IN_PROGRESS,
							})
						} else {
							//API failed case
							setValues({
								payment_status:
									PaymentStatus.SERVICE_UNAVAILABLE,
							})
						}
					}
				}
			}

			startPolling()

			return () => {
				cancelled = true
			}
		}
	}, [
		paymentStatus,
		// formState?.order_id,
		// formState?.transaction_id,
		// paymentStatusWatcher,
	])

	useEffect(() => {
		// only set default if nothing is selected yet and data is now available
		// if (!selectedPaymentPlan) {

		if (
			displayTheSelfAndLoanPaymentModesTogether &&
			(selectedPaymentPlan === "" || selectedPaymentPlan === undefined)
		) {
			if (activeSelfPlans.length > 0) {
				setSelectedPaymentPlan(
					`self-${activeSelfPlans[0].component_id}`,
				)
				setValues({
					payment_type: "self",
				})
			} else if (activeLoanPlans.length > 0) {
				setSelectedPaymentPlan(
					`loan-${activeLoanPlans[0].component_id}`,
				)
				setValues({
					payment_type: "loan",
				})
			}
		} else if (
			!displayTheSelfAndLoanPaymentModesTogether &&
			activeSelfPlans?.length > 0 &&
			(selectedPaymentPlan === "" ||
				selectedPaymentPlan === undefined ||
				(activeTab === "self-payment" &&
					selectedPaymentPlan.startsWith("loan-")))
		) {
			setSelectedPaymentPlan(`self-${activeSelfPlans[0].component_id}`)
			setValues({
				payment_type: "self",
			})
		} else if (
			!displayTheSelfAndLoanPaymentModesTogether &&
			activeLoanPlans?.length > 0 &&
			(selectedPaymentPlan === "" ||
				selectedPaymentPlan === undefined ||
				(activeTab === "loan-payment" &&
					selectedPaymentPlan.startsWith("self-")))
		) {
			setSelectedPaymentPlan(`loan-${activeLoanPlans[0].component_id}`)
			setValues({
				payment_type: "loan",
			})
		}
	}, [activeSelfPlans, activeLoanPlans, selectedPaymentPlan, activeTab])

	// const stateValues = getValues()

	// useEffect(() => {
	// 	console.log("[PAYMENT-STATUS]payment status changed to: ", formState)
	// }, [formState])

	const handleDownloadReceipt = async () => {
		try {
			setIsLoading(true)
			const orderId = getValues()?.order_id
			const response = await api.get(
				`/applications/${applicationId}/orders/${orderId}/receipt`,
				{
					responseType: "blob",
				},
			)

			if (response.status !== 200) {
				throw new Error("Failed to download receipt")
			}

			let fileData = response.data
			if (!fileData || fileData.size === 0) {
				// fallback dummy PDF data
				const dummyPdfBytesData = dummyPdfBytes

				fileData = new Blob([dummyPdfBytesData], {
					type: "application/pdf",
				})
			}

			const blob =
				fileData instanceof Blob
					? fileData
					: new Blob([fileData], { type: "application/pdf" })
			const url = window.URL.createObjectURL(blob)

			const a = document.createElement("a")
			a.href = url
			a.download = `receipt_001_${Date.now()}.pdf`
			document.body.appendChild(a)
			a.click()
			a.remove()
			window.URL.revokeObjectURL(url)
		} catch {
			alert("Failed to download receipt")
		} finally {
			setIsLoading(false)
		}
	}

	const handleDowloadCreditNote = async () => {
		try {
			setIsLoading(true)
			const orderId = getValues()?.order_id
			await downloadFileFromURl(
				`/applications/${applicationId}/orders/${orderId}/credit-note`,
				`credit-note_${applicationId}_${format(Date.now(), "dd-MM-yyyy")}.pdf`,
				"application/pdf",
				dummyPdfBytes,
			)
		} catch (err) {
			alert(err)
		} finally {
			setIsLoading(false)
		}
	}

	const handleDownloadAcknowledgement = async (is_loan: boolean) => {
		const universityId = formConfig?.university_id
		const batchId = formConfig?.batch_id
		const paymentId = paymentStatus?.details?.payment_id
		const loanId = paymentStatus?.details?.loan_id
		if (is_loan) {
			await downloadFile(
				`universities/${universityId}/batches/${batchId}/offline-payments/loans/${loanId}/ack-receipt`,
				`acknowledgement_${applicationId}_${format(Date.now(), "dd-MM-yyyy")}.pdf`,
				setIsLoading,
				"Failed to download acknowledgement",
			)
		} else {
			await downloadFile(
				`universities/${universityId}/batches/${batchId}/offline-payments/self/${paymentId}/ack-receipt`,
				`acknowledgement_${applicationId}_${format(Date.now(), "dd-MM-yyyy")}.pdf`,
				setIsLoading,
				"Failed to download acknowledgement",
			)
		}
	}

	const handleResubmit = (is_loan: boolean) => {
		if (is_loan) {
			setActiveTab("loan-payment")
			// set the selected payment plan based on the payment status response plan
			// filter out the component_id from the payment status response plan from the activeLoanPlans
			const selectedLoanPlan = paymentStatus?.details?.plan
			const planId = activeLoanPlans.find(
				plan => plan.name === selectedLoanPlan,
			)?.component_id
			if (!planId) {
				setSelectedPaymentPlan(`loan-${planId}`)
			}
			form.setValue(config.field_id + ".payment_type", "loan", {
				shouldValidate: false,
				shouldDirty: false,
				shouldTouch: false,
			})
			form.setValue(
				config.field_id + ".loan_payment_mode",
				"loan-disbursed",
				{
					shouldValidate: false,
					shouldDirty: false,
					shouldTouch: false,
				},
			)
			loanOfflinePaymentForm.reset({
				proof_of_loan_disbursal: undefined,
			})
		} else {
			setActiveTab("self-payment")
			// set the selected payment plan based on the payment status response plan
			// filter out the component_id from the payment status response plan from the activeSelfPlans
			const selectedSelfPlan = paymentStatus?.details?.plan
			const planId = activeSelfPlans.find(
				plan => plan.name === selectedSelfPlan,
			)?.component_id
			if (!planId) {
				setSelectedPaymentPlan(`self-${planId}`)
			}
			form.setValue(config.field_id + ".payment_type", "self", {
				shouldValidate: false,
				shouldDirty: false,
				shouldTouch: false,
			})
			form.setValue(
				config.field_id + ".self_payment_mode",
				"already-paid",
				{
					shouldValidate: false,
					shouldDirty: false,
					shouldTouch: false,
				},
			)
			selfOfflinePaymentForm.reset({
				proof_of_payment: undefined,
			})
		}
		form.setValue(
			config.field_id + ".payment_status",
			is_loan
				? PaymentStatus.OFFLINE_LOAN_REJECTED_SHOW_STATUS
				: PaymentStatus.OFFLINE_PAYMENT_REJECTED_SHOW_STATUS,
			{
				shouldValidate: false,
				shouldDirty: false,
				shouldTouch: false,
			},
		)
		// if (formState?.payment_type === "self") {
		// 	selfOfflinePaymentForm.reset({
		// 		proof_of_payment: undefined,
		// 	})
		// } else if (is_loan) {
		// 	loanOfflinePaymentForm.reset({
		// 		proof_of_loan_disbursal: undefined,
		// 	})
		// }

		// if (is_loan) {

		// } else {
		// 	form.setValue(config.field_id + ".payment_type", "self")
		// 	form.setValue(
		// 		config.field_id + ".self_payment_mode",
		// 		"already-paid",
		// 	)
		// 	setSelectedPaymentPlan(`self-${activeSelfPlans[0].component_id}`)
		// }
	}

	const showCoupon = useMemo(() => {
		const fee = selectedPaymentPlanFee.find(
			fee =>
				fee.component_id ===
				selectedPaymentPlan.replace(/^(self|loan)-/, ""),
		)

		if (!fee || !fee.frequency || !fee?.coupon_discount) return false

		// return checkPaymentPlan(
		// 	fee?.frequency as PAYMENT_PLAN,
		// 	realData?.coupon_discount,
		// 	getValues()?.payment_type,
		// 	loanProviderForm.watch("loan_provider_id"),
		// 	loanProviderForm.watch("loan_provider_tenure"),
		// )
		return true
	}, [
		selectedPaymentPlan,
		selectedPaymentPlanFee,
		realData?.coupon_discount,
		getValues()?.payment_type,
	])

	const renderStatus = () => {
		switch (form.watch(config.field_id + ".payment_status")) {
			case PaymentStatus.PAYMENT_SUCCESS:
				return (
					<Alert
						title="Payment Success"
						message="Your payment has been processed successfully."
						status={PaymentStatus.PAYMENT_SUCCESS}
					/>
				)
			case PaymentStatus.LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT:
				setFormDisabled(true)
				return (
					<Alert
						title="Loan Approved Pending"
						message="Awaiting Disbursement. Kindly check back to view the Loan Application updates here.."
						status={
							PaymentStatus.LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT
						}
					/>
				)
			case PaymentStatus.ORDER_CREATED_PENDING_LOAN_APPROVAL:
				setFormDisabled(true)
				return (
					<Alert
						title="Loan Applied"
						message="Kindly check back to view the Loan Application updates here.."
						status={
							PaymentStatus.ORDER_CREATED_PENDING_LOAN_APPROVAL
						}
					/>
				)
			case PaymentStatus.ORDER_CREATED_LOAN_REJECTED:
				setFormDisabled(false)
				return (
					<Alert
						title="Loan Rejected"
						message="Loan is Rejected. Retry for Self payment. "
						status={PaymentStatus.ORDER_CREATED_LOAN_REJECTED}
					/>
				)
			case PaymentStatus.LOAN_DISBURSEMENT_RECEIVED:
				return (
					<Alert
						title="Loan Disbursed"
						message="Your loan has been disbursed successfully."
						status={PaymentStatus.LOAN_DISBURSEMENT_RECEIVED}
					/>
				)
			default:
				return null
		}
	}

	const handleRetryTrigger = () => {
		setValues({
			payment_status: PaymentStatus.ORDER_CREATED_PENDING_PAYMENT,
		})
	}

	useEffect(() => {
		if (typeof error === "object" && error !== null) {
			if ((error as AxiosError)?.response?.status === 500) {
				form.setValue(
					"payment_status",
					PaymentStatus.SERVICE_UNAVAILABLE,
				)
			}
		}
	}, [error])

	useEffect(() => {
		if (loanDetails?.applicant?.additional_details?.plan_id) {
			form.setValue(config.field_id + ".payment_type", "loan")
		}
	}, [loanDetails])

	// Check the partner type is IST
	const isISTPartner = window.__DF_USER__?.partner_type === "INSIDE_SALES"
	const isCounselorOrPartner =
		window.__DF_USER__?.type === "COUNSELLOR" ||
		window.__DF_USER__?.type === "PARTNER"

	const renderDisplay = () => {
		switch (form.watch(config.field_id + ".payment_status")) {
			case PaymentStatus.PAYMENT_SUCCESS:
				return (
					<PaymentSuccess
						transactionId={
							form.watch(config.field_id)?.transaction_id
						}
						amount={String(
							paymentStatus?.details?.amount ||
								totalAmountAfterDiscount,
						)}
						currency={
							paymentStatus?.details?.currency ??
							selectedPaymentPlanFee[0]?.currency
						}
						paymentPreference={
							paymentStatus?.details?.plan ??
							getPlanType(selectedPaymentPlanFee[0]?.frequency)
						}
						onDownloadReceipt={handleDownloadReceipt}
						isLoading={isLoading}
						hideDownloadReceipt={
							form.watch(config.field_id)?.hideDownloadReceipt
						}
						paidAt={form.watch(config.field_id)?.paidAt || ""}
					/>

					// <Status
					// 	icon={
					// 		<CheckCircle2 className="h-12 w-12 text-green-600" />
					// 	}
					// 	mapEntries={{
					// 		"Payment Id": getFieldValues(config.field_id)
					// 			?.transaction_id,
					// 		Amount:
					// 			selectedPaymentPlanFee[0]?.currency +
					// 				" " +
					// 				getFieldValues(config.field_id)
					// 					?.final_payable_amount || "0",
					// 	}}
					// 	hasDownloads={true}
					// 	DownloadComponent={() => (
					// 		<Button
					// 			type="button"
					// 			onClick={handleDownloadReceipt}
					// 		>
					// 			Download receipt
					// 		</Button>
					// 	)}
					// 	payment_type="self"
					// 	status="SUCCESS"
					// 	heading="Payment Successful!"
					// 	message="Your payment has been processed successfully."
					// />
				)
			case PaymentStatus.PAYMENT_FAILED_RETRY_ALLOWED:
				return (
					<div className="flex flex-col items-center gap-3">
						<div className="mb-6 rounded-md bg-yellow-50 p-4">
							<div className="flex">
								<div className="flex-shrink-0">
									<svg
										className="h-5 w-5 text-yellow-400"
										viewBox="0 0 20 20"
										fill="currentColor"
									>
										<path
											fillRule="evenodd"
											d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
											clipRule="evenodd"
										/>
									</svg>
								</div>
								<div className="ml-3">
									<h3 className="text-sm font-medium text-yellow-800">
										Payment Failed
									</h3>
									<div className="mt-2 text-sm text-yellow-700">
										<p>
											Your previous payment attempt
											failed. Please try again.
										</p>
									</div>
								</div>
							</div>
						</div>
						<Button
							type="button"
							onClick={handleRetryTrigger}
						>
							Retry Payment
						</Button>
					</div>
				)
			case PaymentStatus.PAYMENT_IN_PROGRESS:
				return (
					<div className="flex flex-col items-center justify-center space-y-3 rounded-2xl border bg-white p-8 shadow-sm">
						<Loader2 className="h-6 w-6 animate-spin text-blue-600" />
						<span className="text-sm font-medium text-gray-700">
							Processing payment please wait...
						</span>
						<Button
							type="button"
							onClick={() => {
								window.location.reload()
							}}
						>
							Refresh
						</Button>
						<p className="text-sm text-gray-500">
							If you have already paid, please check back after a
							few minutes or refresh to get payment status.
						</p>
					</div>
				)
			case PaymentStatus.SERVICE_UNAVAILABLE:
				return (
					<div className="mx-auto max-w-md overflow-hidden rounded-xl bg-white shadow-md md:max-w-2xl">
						<div className="md:flex">
							<div className="p-8">
								<div className="mb-4 flex items-center justify-center">
									<div className="rounded-full bg-red-100 p-3">
										<svg
											className="h-8 w-8 text-red-600"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth="2"
												d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
											/>
										</svg>
									</div>
								</div>
								<h2 className="mb-2 text-center text-2xl font-bold text-gray-800">
									Service Unavailable
								</h2>
								<p className="mb-6 text-center text-gray-600">
									We're experiencing technical difficulties.
									Our team has been notified and is working to
									resolve the issue.
								</p>
								<div className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 p-4">
									<div className="flex">
										<div className="flex-shrink-0">
											<svg
												className="h-5 w-5 text-yellow-400"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
													clipRule="evenodd"
												/>
											</svg>
										</div>
										<div className="ml-3">
											<p className="text-sm text-yellow-700">
												Please try again in a few
												minutes. If the problem
												persists, contact our support
												team.
											</p>
										</div>
									</div>
								</div>
								<div className="flex justify-center">
									<button
										type="button"
										onClick={() => window.location.reload()}
										className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
									>
										<svg
											className="-ml-1 mr-2 h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
											/>
										</svg>
										Refresh Page
									</button>
								</div>
							</div>
						</div>
					</div>
				)

			case PaymentStatus.LOAN_DISBURSEMENT_RECEIVED:
				return (
					<div>
						<Status
							iconBg="bg-green-100"
							icon={
								<CheckCircle2 className="h-12 w-12 text-green-600" />
							}
							mapEntries={{
								"Transaction ID":
									(paymentStatus?.details?.transaction_id &&
										paymentStatus?.details
											?.transaction_id) ||
									"-",
								"Loan Amount":
									(loanDetails?.currency &&
										loanDetails?.currency) +
										" " +
										(loanDetails?.loan_amount &&
											loanDetails?.loan_amount.toString()) ||
									"-",
								"Loan Provider":
									// Filter out the lender_display_name from the loanProviders array
									loanProviders?.find(
										provider =>
											provider.lender_id ===
											loanDetails?.lender_id,
									)?.display_name || "-",
								"Loan Tenure":
									(loanDetails?.tenure_in_months &&
										loanDetails?.tenure_in_months.toString()) ||
									"-",
								"Time & Date": paymentStatus?.details
									?.updated_at
									? format(
											new Date(
												paymentStatus?.details
													?.updated_at || "",
											),
											"dd MMM yyyy | hh:mm aa",
										) || "-"
									: "-",
							}}
							hasDownloads={true}
							DownloadComponent={() =>
								window.__DF_USER__ ? (
									(isCounselorOrPartner && isISTPartner) ||
									!isCounselorOrPartner ? (
										<Button
											variant="customDefault"
											type="button"
											className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold text-green-600"
											onClick={handleDowloadCreditNote}
											disabled={isLoading}
										>
											{isLoading ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													<span>
														Downloading Receipt...
													</span>
												</>
											) : (
												<>
													<Download className="h-5 w-5" />
													<span>Get Credit Note</span>
												</>
											)}
										</Button>
									) : null
								) : (
									<Button
										variant="customDefault"
										type="button"
										className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold text-green-600"
										onClick={handleDowloadCreditNote}
										disabled={isLoading}
									>
										{isLoading ? (
											<>
												<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												<span>
													Downloading Receipt...
												</span>
											</>
										) : (
											<>
												<Download className="h-5 w-5" />
												<span>Get Credit Note</span>
											</>
										)}
									</Button>
								)
							}
							payment_type="loan"
							status={PaymentStatus.LOAN_DISBURSEMENT_RECEIVED}
							heading="Thank You!"
							message="Your loan has been disbursed successfully."
						/>
						<br />
						{renderStatus()}
					</div>
				)

			// Offline Payment Status (Self)
			case PaymentStatus.OFFLINE_PAYMENT_PENDING_VERIFICATION:
				return (
					<div>
						<Status
							iconBg="bg-green-100"
							icon={
								<CheckCircle2 className="h-12 w-12 text-green-600" />
							}
							mapEntries={{
								"Transaction ID":
									paymentStatus?.details?.reference_number ||
									"-",
								Amount:
									selectedPaymentPlanFee[0]?.currency +
									" " +
									(paymentStatus?.details?.amount || "-"),
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",
								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-yellow-500" />
										<span className="text-yellow-600">
											PENDING
										</span>
									</div>
								),
								Date:
									(
										paymentStatus?.details?.paid_at &&
										format(
											new Date(
												paymentStatus?.details
													?.paid_at || "",
											),
											"dd MMM yyyy",
										)
									)?.toUpperCase() || "-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={() =>
										handleDownloadAcknowledgement(false)
									}
									disabled={isLoading}
								>
									{isLoading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											<span>Downloading Receipt...</span>
										</>
									) : (
										<>
											<Download className="h-5 w-5" />
											<span className="hover:underline">
												Get Acknowledgement Receipt
											</span>
										</>
									)}
								</Button>
							)}
							payment_type="offline"
							status={
								PaymentStatus.OFFLINE_PAYMENT_PENDING_VERIFICATION
							}
							heading="Payment Details Submitted"
							message="Your offline payment details has been submitted successfully."
						/>
						<br />
						{renderStatus()}
					</div>
				)

			case PaymentStatus.OFFLINE_PAYMENT_APPROVED:
				return (
					<div>
						<Status
							iconBg="bg-green-100"
							icon={
								<CheckCircle2 className="h-12 w-12 text-green-600" />
							}
							updated_at={
								paymentStatus?.details?.payment_updated_at
							}
							mapEntries={{
								"Transaction ID":
									paymentStatus?.details?.transaction_id ||
									"-",
								Amount:
									selectedPaymentPlanFee[0]?.currency +
									" " +
									(paymentStatus?.details?.amount || "-"),
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",
								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
										<span className="text-green-600">
											VERIFIED
										</span>
									</div>
								),
								Date:
									(
										paymentStatus?.details?.paid_at &&
										format(
											new Date(
												paymentStatus?.details
													?.paid_at || "",
											),
											"dd MMM yyyy",
										)
									)?.toUpperCase() || "-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={handleDownloadReceipt}
									disabled={isLoading}
								>
									{isLoading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											<span>Downloading Receipt...</span>
										</>
									) : (
										<>
											<Download className="h-5 w-5" />
											<span className="hover:underline">
												Get PDF Receipt
											</span>
										</>
									)}
								</Button>
							)}
							payment_type="offline"
							status={PaymentStatus.OFFLINE_PAYMENT_APPROVED}
							heading="Payment Details Verified"
							message="Your payment has been verified and approved by our finance team"
						/>
						<br />
						{renderStatus()}
					</div>
				)

			case PaymentStatus.OFFLINE_PAYMENT_REJECTED:
				return (
					<div>
						<Status
							iconBg="bg-red-100"
							icon={
								<CircleX className="h-12 w-12 text-red-600" />
							}
							mapEntries={{
								"Transaction ID":
									paymentStatus?.details?.transaction_id ||
									"-",
								Amount:
									selectedPaymentPlanFee[0]?.currency +
									" " +
									(paymentStatus?.details?.amount || "-"),
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",
								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-[#F04438]" />
										<span className="text-[#F04438]">
											REJECTED
										</span>
									</div>
								),
								Date:
									(
										paymentStatus?.details?.paid_at &&
										format(
											new Date(
												paymentStatus?.details
													?.paid_at || "",
											),
											"dd MMM yyyy",
										)
									)?.toUpperCase() || "-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={() => handleResubmit(false)}
								>
									<>
										<RefreshCw className="h-5 w-5" />
										<span className="hover:underline">
											Resubmit Payment
										</span>
									</>
								</Button>
							)}
							payment_type="offline"
							status={PaymentStatus.OFFLINE_PAYMENT_REJECTED}
							heading="Payment Details Rejected "
							message="Your payment verification was unsuccessful"
							remarks={paymentStatus?.details?.comments}
						/>
						<br />
						{renderStatus()}
					</div>
				)
			// Offline Payment Status (Loan)
			case PaymentStatus.OFFLINE_LOAN_PENDING_VERIFICATION:
				return (
					<div>
						<Status
							iconBg="bg-green-100"
							icon={
								<CheckCircle2 className="h-12 w-12 text-green-600" />
							}
							mapEntries={{
								"Loan ID":
									paymentStatus?.details?.loan_id || "-",
								"Loan Amount":
									selectedPaymentPlanFee[0]?.currency +
										" " +
										(paymentStatus?.details?.loan_amount &&
											paymentStatus?.details?.loan_amount.toString()) ||
									"-",
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",

								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-yellow-500" />
										<span className="text-yellow-600">
											PENDING
										</span>
									</div>
								),
								"Loan Vendor":
									(paymentStatus?.details?.lender &&
										paymentStatus?.details?.lender) ||
									"-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={() =>
										handleDownloadAcknowledgement(true)
									}
									disabled={isLoading}
								>
									{isLoading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											<span>Downloading Receipt...</span>
										</>
									) : (
										<>
											<Download className="h-5 w-5" />
											<span className="hover:underline">
												Get Acknowledgement Receipt
											</span>
										</>
									)}
								</Button>
							)}
							payment_type="offline"
							status={
								PaymentStatus.OFFLINE_PAYMENT_PENDING_VERIFICATION
							}
							heading="Loan Details Submitted"
							message="Your offline loan details have been received successfully"
						/>
						<br />
						{renderStatus()}
					</div>
				)

			case PaymentStatus.OFFLINE_LOAN_APPROVED:
				return (
					<div>
						<Status
							iconBg="bg-green-100"
							icon={
								<CheckCircle2 className="h-12 w-12 text-green-600" />
							}
							updated_at={paymentStatus?.details?.loan_updated_at}
							mapEntries={{
								"Loan ID":
									paymentStatus?.details?.loan_id || "-",
								"Loan Amount":
									selectedPaymentPlanFee[0]?.currency +
										" " +
										(paymentStatus?.details?.loan_amount &&
											paymentStatus?.details?.loan_amount.toString()) ||
									"-",
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",

								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
										<span className="text-green-600">
											VERIFIED
										</span>
									</div>
								),
								"Loan Vendor":
									(paymentStatus?.details?.lender &&
										paymentStatus?.details?.lender) ||
									"-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={handleDownloadReceipt}
									disabled={isLoading}
								>
									{isLoading ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											<span>Downloading Receipt...</span>
										</>
									) : (
										<>
											<Download className="h-5 w-5" />
											<span className="hover:underline">
												Get PDF Receipt
											</span>
										</>
									)}
								</Button>
							)}
							payment_type="offline"
							status={PaymentStatus.OFFLINE_PAYMENT_APPROVED}
							heading="Loan Details Verified"
							message="Your offline loan details request has been verified and approved by our finance team"
						/>
						<br />
						{renderStatus()}
					</div>
				)

			case PaymentStatus.OFFLINE_LOAN_REJECTED:
				return (
					<div>
						<Status
							iconBg="bg-red-100"
							icon={
								<CircleX className="h-12 w-12 text-red-600" />
							}
							mapEntries={{
								"Loan ID":
									paymentStatus?.details?.loan_id || "-",
								"Loan Amount":
									selectedPaymentPlanFee[0]?.currency +
										" " +
										(paymentStatus?.details?.loan_amount &&
											paymentStatus?.details?.loan_amount.toString()) ||
									"-",
								"Fee Payment Preference":
									paymentStatus?.details?.plan || "-",

								Status: (
									<div className="flex items-center justify-end gap-2">
										<div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
										<span className="text-red-600">
											REJECTED
										</span>
									</div>
								),
								"Loan Vendor":
									(paymentStatus?.details?.lender &&
										paymentStatus?.details?.lender) ||
									"-",
							}}
							hasDownloads={true}
							DownloadComponent={() => (
								<Button
									variant="customDefault"
									type="button"
									className="flex w-full items-center justify-center space-x-2 py-3 text-lg font-semibold"
									onClick={() => handleResubmit(true)}
								>
									<>
										<RefreshCw className="h-5 w-5" />
										<span className="hover:underline">
											Resubmit Loan Details
										</span>
									</>
								</Button>
							)}
							payment_type="offline"
							status={PaymentStatus.OFFLINE_LOAN_REJECTED}
							heading="Loan Details Rejected"
							message="Your offline Loan Details could not be verified."
							remarks={
								paymentStatus?.details?.loan_comments || ""
							}
						/>
						<br />
						{renderStatus()}
					</div>
				)
			case PaymentStatus.OFFLINE_PAYMENT_REJECTED_SHOW_STATUS:
			case PaymentStatus.OFFLINE_LOAN_REJECTED_SHOW_STATUS:
			default:
				return (
					!isPaymentStatusLoading && (
						<form className="space-y-5">
							<Payment.Plans
								form={form}
								paymentStatus={paymentStatus}
							/>
							{form.watch(config.field_id + ".payment_type") ==
								"loan" &&
								enableOfflineLoan && (
									<div className="my-4 flex items-center space-x-2">
										<p className="text-base">
											Loan Disbursed Already
										</p>
										<Switch
											checked={
												form.watch(
													config.field_id +
														".loan_payment_mode",
												) === "loan-disbursed"
											}
											disabled={formDisabled}
											onCheckedChange={(
												value: boolean,
											) => {
												form.setValue(
													config.field_id +
														".loan_payment_mode",
													value
														? "loan-disbursed"
														: "apply-loan",
												)
											}}
											id="loan-disbursal"
											className="data-[state=unchecked]:bg-gray-300"
										/>
									</div>
								)}
							{form.watch(config.field_id + ".payment_type") ==
								"loan" &&
								form.watch(
									config.field_id + ".loan_payment_mode",
								) !== "loan-disbursed" && (
									<Form {...loanProviderForm}>
										{" "}
										<Payment.LoanProviderForm
											form={form}
											paymentStatus={paymentStatus}
											isLoadingLoanDetails={
												isPaymentStatusLoading
											}
										/>
									</Form>
								)}

							{showCoupon &&
								realData?.coupon_discount &&
								realData.coupon_discount.discount > 0 && (
									<Coupons
										coupon_discount={
											realData?.coupon_discount
										}
										currency={
											realData.components?.length > 0
												? realData.components[0]
														.currency
												: "INR"
										}
									/>
								)}
							<Summary
								nameYourPaymentPlan={nameYourPayment}
								description={description}
							/>
							{enableOfflinePayment &&
								(formState?.payment_type == "self" ? (
									<Form {...selfOfflinePaymentForm}>
										<Payment.OfflinePayment
											payment_id={
												paymentStatus?.details
													?.payment_id || ""
											}
											finalAmout={
												totalAmountAfterDiscount
											}
										/>
									</Form>
								) : (
									<Form {...loanOfflinePaymentForm}>
										<Payment.OfflinePayment
											payment_id={
												paymentStatus?.details
													?.payment_id || ""
											}
											finalAmout={
												totalAmountAfterDiscount
											}
										/>
									</Form>
								))}
							{form.watch(config.field_id + ".payment_status") ==
								PaymentStatus.OFFLINE_PAYMENT_REJECTED_SHOW_STATUS && (
								<div
									className={cn(
										"my-2 flex items-center gap-2",
									)}
								>
									<AlertStatus
										className={cn(
											"flex h-full w-full gap-2 border-none",
											"bg-red-50",
										)}
									>
										<div>
											{
												<Info
													className={`text-red-600`}
												/>
											}
										</div>
										<div className="flex flex-col space-y-3">
											<AlertTitle
												className={cn(
													"text-left",
													"text-red-800",
												)}
											>
												Your payment could not be
												verified.
											</AlertTitle>
											<AlertDescription
												className={cn(
													"text-left",
													"text-red-800",
												)}
											>
												<>
													<span className="font-bold">
														Reason
													</span>{" "}
													:{" "}
													{
														paymentStatus?.details
															?.comments
													}
													. <br />
												</>
											</AlertDescription>
										</div>
									</AlertStatus>
								</div>
							)}
							{form.watch(config.field_id + ".payment_status") ==
								PaymentStatus.OFFLINE_LOAN_REJECTED_SHOW_STATUS && (
								<div
									className={cn(
										"my-2 flex items-center gap-2",
									)}
								>
									<AlertStatus
										className={cn(
											"flex h-full w-full gap-2 border-none",
											"bg-red-50",
										)}
									>
										<div>
											{
												<Info
													className={`text-red-600`}
												/>
											}
										</div>
										<div className="flex flex-col space-y-3">
											<AlertTitle
												className={cn(
													"text-left",
													"text-red-800",
												)}
											>
												Your payment could not be
												verified.
											</AlertTitle>
											<AlertDescription
												className={cn(
													"text-left",
													"text-red-800",
												)}
											>
												<>
													<span className="font-bold">
														Reason
													</span>{" "}
													:{" "}
													{
														paymentStatus?.details
															?.loan_comments
													}
													. <br />
												</>
											</AlertDescription>
										</div>
									</AlertStatus>
								</div>
							)}
							<Payment.Actions
								form={form}
								config={config}
								programTypeFieldId={programTypeFieldId!}
								periodNumber={periodNumber}
							/>
							{renderStatus()}
						</form>
					)
				)
		}
	}

	const state = {
		fieldConfig: config,
		activeTab,
		setActiveTab,
		formState: { ...formState },
		loanProviderForm: {
			form: loanProviderForm,
		},
		loanOfflinePaymentForm: {
			form: loanOfflinePaymentForm,
		},
		selfOfflinePaymentForm: {
			form: selfOfflinePaymentForm,
		},
		displayTheSelfAndLoanPaymentModesTogether:
			displayTheSelfAndLoanPaymentModesTogether || false,
		enableOfflinePayment: enableOfflinePayment || false,
		getValues,
		setValues,
		validate,
		selectedPaymentPlan,
		paymentPlansPrices,
		setSelectedPaymentPlan,
		activeLoanPlans,
		activeSelfPlans,
		feeSummary: selectedPaymentPlanFee,
		applicatioDiscount: realData?.application_discount,
		couponDiscount: realData?.coupon_discount,
		isPreviewMode: isPreview,
		course_id: realData?.course_id || "",
		periodNumber: periodNumber,
	}

	return (
		<PaymentContext.Provider value={state}>
			<FormField
				name={config.field_id}
				control={form.control}
				render={() => (
					<FormItem>
						<FormLabel>Payment</FormLabel>
						<FormControl>
							{isPaymentStatusLoading ? (
								<div className="flex h-full w-full items-center justify-center">
									<Loader2 className="h-5 w-5 animate-spin" />
								</div>
							) : (
								<div className="flex flex-col gap-5">
									{renderDisplay()}
								</div>
							)}
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</PaymentContext.Provider>
	)
}

Payment.Plans = Plans
Payment.LoanProviderForm = LoanProviderForm
Payment.Actions = Actions
Payment.OfflinePayment = OfflinePayment

export default Payment
